import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  BannerTypes,
  LoadingIndicator,
  Snackbar,
  UtilityConnectionContainer,
  WizardCard,
} from "@sunrun/experience-ui-components";
import { useParams } from "react-router-dom";
import type { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { CheckoutFooter } from "../../molecules/CheckoutFooter";
import { CheckoutEditSignersSlideout } from "../CheckoutEditSignersSlideout";
import { CheckoutUtilityBillSlideout } from "../CheckoutUtilityBillSlideout";
import {
  steps,
  MISSING_UTILITY_BILL_BANNER_TITLE,
  UTILITY_MISMATCH_BANNER_TITLE,
  NAME_ON_BILL_UNKNOWN,
  mismatchModalConfigByStep,
} from "./constants";
import {
  formatContactName,
  anyNamesMatch,
  buildTitleMismatchMessage,
  getMostRecentAttachmentUrl,
  hasContractReviewBanners,
} from "./helpers";
import { NameComparisonStep, NameOnBillNotFound } from "./NameComparisonStep";
import { ContactInfoStep } from "./ContactInfoStep";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import { ErrorModal } from "components/molecules/ErrorModal";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { useContactManagement } from "hooks/useContactManagement";
import { usePrevious } from "hooks/usePrevious";
import { useCheckoutAttachments } from "checkout/hooks/useCheckoutSwr";
import { useAuthToken } from "hooks/useAuthToken";
import { confirmContacts } from "services/contacts";
import { useGetUtilityConnectionUrl } from "hooks/useGetUtilityConnectionUrl";
import { useGetUtilityConnectionStatus } from "hooks/useGetUtilityConnectionStatus";
import { useUsageData } from "utils/swrHooks";
import {
  findContact,
  getNamesOnTitle,
  getPrimarySignerContact,
} from "utils/contacts";
import { isEmptyValue, ROLES } from "utils/usageUtils";
import { formatPhoneNumber } from "utils/formatPhoneNumber";
import {
  isDuplicateEmailValidationStatus,
  isDuplicatePhoneValidationStatus,
} from "utils/contactValidation";
import {
  isNemAgreementCheckMessage,
  isTrustAgreementNameMatchMessage,
} from "utils/contractReviewUtils";
import { Mixpanel } from "providers/mixpanel";
import {
  CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_NAMES_TAB_CHECKOUT_PAGE,
  CUSTOMER_SELF_CHECKOUT__RISK_RE_SIGNING_FROM_CONFIRM_NAMES_MODAL,
} from "constants/mixpanelEvents";
import { ResignRiskModal } from "checkout/components/molecules/ResignRiskModal";

const ConfirmNamesV2: React.FC = () => {
  const { prospectId, offerId } = useParams();
  const {
    navigateToNextTask,
    refreshAndNavigateNext,
    currentTask,
    hasUploadedUtilityBill,
    proposalId,
    contractReviewMessages,
  } = useCheckoutContext();
  const authKey = useAuthToken("HYBRID")?.token || "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const [error, setError] = useState<{ action?: string; error?: Error }>();
  const [isSaving, setIsSaving] = useState(false);
  const [wizardCurrentStep, setWizardCurrentStep] = useState(0);
  const [isEditSignersSlideoutOpen, setIsEditSignersSlideoutOpen] =
    useState(false);
  const [isMismatchModalOpen, setIsMismatchModalOpen] = useState(false);
  const [isUtilityBillSlideoutOpen, setIsUtilityBillSlideoutOpen] =
    useState(false);
  const [showGetBillOnlineIframe, setShowGetBillOnlineIframe] = useState(false);
  const [showUploadSuccessToast, setShowUploadSuccessToast] = useState(false);
  const [showUpdateInfoSnackbar, setShowUpdateInfoSnackbar] = useState(false);
  const [updateInfoSnackbarMessage, setUpdateInfoSnackbarMessage] =
    useState("");
  const [isContactInfoConfirmed, setIsContactInfoConfirmed] = useState(false);

  const { prospect, isLoading: isProspectLoading } = useUsageData(prospectId);
  const {
    getUrl,
    loading: billOnlineLoading,
    url: billOnlineUrl,
  } = useGetUtilityConnectionUrl(authKey);
  const {
    status: intervalDataStatus,
    initialLoading: intervalDataInitialLoading,
    startPolling,
  } = useGetUtilityConnectionStatus(authKey, prospectId || null);

  const isMissingUtilityBill = !hasUploadedUtilityBill;

  const { data: utilityBillDocuments } = useCheckoutAttachments(
    prospectId ?? "",
    proposalId ?? "",
    "Utility Bill"
  );

  const mostRecentUtilityBillUrl = useMemo(
    () => getMostRecentAttachmentUrl(utilityBillDocuments),
    [utilityBillDocuments]
  );

  const {
    contactList: contacts,
    isContactsLoading,
    refetch,
  } = useContactManagement();

  const selectedPrimaryContact = getPrimarySignerContact(
    contacts,
    ROLES.HOMEOWNER
  );
  const selectedUtilityContact = findContact(
    contacts,
    ROLES.UTILITYBILLCONTACT
  );
  const selectedSecondaryContact = findContact(
    contacts,
    ROLES.CONTRACTCOSIGNER
  );

  const previousPrimaryContact = usePrevious(selectedPrimaryContact);
  const [originalPrimaryContact, setOriginalPrimaryContact] =
    useState<Contact>();
  const previousUtilityContact = usePrevious(selectedUtilityContact);
  const [originalUtilityContact, setOriginalUtilityContact] =
    useState<Contact>();
  const previousSecondaryContact = usePrevious(selectedSecondaryContact);
  const [originalSecondaryContact, setOriginalSecondaryContact] =
    useState<Contact>();

  useEffect(() => {
    if (!previousPrimaryContact && selectedPrimaryContact) {
      setOriginalPrimaryContact(selectedPrimaryContact);
    }
  }, [previousPrimaryContact, selectedPrimaryContact]);

  useEffect(() => {
    if (!previousUtilityContact && selectedUtilityContact) {
      setOriginalUtilityContact(selectedUtilityContact);
    }
  }, [previousUtilityContact, selectedUtilityContact]);

  useEffect(() => {
    if (!previousSecondaryContact && selectedSecondaryContact) {
      setOriginalSecondaryContact(selectedSecondaryContact);
    }
  }, [previousSecondaryContact, selectedSecondaryContact]);

  const contactsHaveChanged =
    originalPrimaryContact?.contactId !== selectedPrimaryContact?.contactId ||
    originalUtilityContact?.contactId !== selectedUtilityContact?.contactId ||
    originalSecondaryContact?.contactId !== selectedSecondaryContact?.contactId;

  const contactsHaveInvalidContactInfo =
    isEmptyValue(selectedPrimaryContact?.customerEmail) ||
    isEmptyValue(selectedPrimaryContact?.customerPrimaryPhone) ||
    isEmptyValue(selectedUtilityContact?.customerEmail) ||
    isEmptyValue(selectedUtilityContact?.customerPrimaryPhone) ||
    (!!selectedSecondaryContact?.contactId &&
      (isEmptyValue(selectedSecondaryContact?.customerEmail) ||
        isEmptyValue(selectedSecondaryContact?.customerPrimaryPhone)));

  const signerName = formatContactName(selectedPrimaryContact);
  const signerNames = useMemo(
    () => (signerName ? [signerName] : []),
    [signerName]
  );
  const namesOnTitle = useMemo(() => getNamesOnTitle(prospect), [prospect]);

  const nemSignerName = formatContactName(selectedUtilityContact);
  const nemSignerNames = useMemo(
    () => (nemSignerName ? [nemSignerName] : []),
    [nemSignerName]
  );

  const namesOnUtilityBill = useMemo<string[]>(() => {
    const name = prospect?.nameOnBill?.trim();
    return name ? [name] : [];
  }, [prospect]);

  const titleMismatchBanner = useMemo(
    () =>
      !anyNamesMatch(signerNames, namesOnTitle)
        ? {
            title: buildTitleMismatchMessage(signerName, namesOnTitle),
            bannerTestId: "confirm-names-title-mismatch-banner",
          }
        : undefined,
    [signerName, signerNames, namesOnTitle]
  );

  const utilityMismatchBanner = useMemo(
    () =>
      !anyNamesMatch(nemSignerNames, namesOnUtilityBill)
        ? {
            title: UTILITY_MISMATCH_BANNER_TITLE,
            bannerTestId: "confirm-names-utility-mismatch-banner",
          }
        : undefined,
    [nemSignerNames, namesOnUtilityBill]
  );

  // When contract-review banners exist for a step, hide the local mismatch banner
  const hasTitleContractReviewBanners = useMemo(
    () =>
      hasContractReviewBanners(
        contractReviewMessages,
        "customer_name_check",
        (msg) => !isTrustAgreementNameMatchMessage(msg)
      ),
    [contractReviewMessages]
  );

  const hasUtilityContractReviewBanners = useMemo(
    () =>
      hasContractReviewBanners(
        contractReviewMessages,
        "nem_agreement_check",
        (msg) => isNemAgreementCheckMessage(msg)
      ),
    [contractReviewMessages]
  );

  const primaryPhone = selectedPrimaryContact?.customerPrimaryPhone?.trim();
  const primaryEmail = selectedPrimaryContact?.customerEmail?.trim();
  const primaryValidationStatus = selectedPrimaryContact?.validationStatus;

  const openEditSignersSlideout = useCallback(() => {
    setIsEditSignersSlideoutOpen(true);
  }, []);

  const openUtilityBillSlideout = useCallback(() => {
    setIsUtilityBillSlideoutOpen(true);
  }, []);

  const onGetBillOnline = useCallback(() => {
    if (prospectId && !intervalDataStatus) {
      getUrl(prospectId);
      setShowGetBillOnlineIframe(true);
    }
  }, [prospectId, intervalDataStatus, getUrl]);

  const handleSlideoutSaveSuccess = useCallback(() => {
    setIsContactInfoConfirmed(false);
  }, []);

  const closeMismatchModal = useCallback(() => {
    setIsMismatchModalOpen(false);
  }, []);

  const handleSignersMatch = useCallback(() => {
    if (titleMismatchBanner) {
      setIsMismatchModalOpen(true);
      return;
    }
    setWizardCurrentStep(1);
  }, [titleMismatchBanner]);

  const handleUtilityMatch = useCallback(() => {
    if (utilityMismatchBanner || isMissingUtilityBill) {
      setIsMismatchModalOpen(true);
      return;
    }
    setWizardCurrentStep(2);
  }, [utilityMismatchBanner, isMissingUtilityBill]);

  const handleRiskResigning = useCallback(() => {
    Mixpanel.track(
      CUSTOMER_SELF_CHECKOUT__RISK_RE_SIGNING_FROM_CONFIRM_NAMES_MODAL,
      {
        prospectId,
        proposalId,
        modalTestId: mismatchModalConfigByStep[wizardCurrentStep]?.modalTestId,
      }
    );
    closeMismatchModal();
    setWizardCurrentStep((step) => Math.min(steps.length - 1, step + 1));
  }, [closeMismatchModal, prospectId, proposalId, wizardCurrentStep]);

  const handleUpdateFromMismatchModal = useCallback(() => {
    const snackbarMessage =
      mismatchModalConfigByStep[wizardCurrentStep]?.updateSnackbarMessage;
    closeMismatchModal();
    openEditSignersSlideout();
    if (snackbarMessage) {
      setUpdateInfoSnackbarMessage(snackbarMessage);
      setShowUpdateInfoSnackbar(true);
    }
  }, [closeMismatchModal, openEditSignersSlideout, wizardCurrentStep]);

  const handleConfirmNames = useCallback(() => {
    if (isCustomer) {
      Mixpanel.track(
        CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_NAMES_TAB_CHECKOUT_PAGE,
        {
          prospectId,
          proposalId,
        }
      );
    }

    if (!selectedPrimaryContact || !selectedUtilityContact) {
      setError({
        action: "Confirm Names Task View",
        error: new Error(
          "You must select a Primary signer and a Utility contact."
        ),
      });
      return;
    }
    if (contactsHaveInvalidContactInfo) {
      setError({
        action: "Confirm Names Task View",
        error: new Error(
          "All selected contacts must both have an email and phone number."
        ),
      });
      return;
    }

    if (!prospectId || !offerId) {
      throw new Error("prospectId or offerId are undefined");
    }

    // If contacts haven't changed, AND ODI isComplete THEN skip confirmContacts
    if (!contactsHaveChanged && currentTask?.isComplete) {
      navigateToNextTask();
      return;
    }

    const contactsArr = [
      {
        contactId: selectedPrimaryContact.contactId,
        role: ROLES.HOMEOWNER,
      },
      {
        contactId: selectedUtilityContact.contactId,
        role: ROLES.UTILITYBILLCONTACT,
      },
    ];
    if (selectedSecondaryContact?.contactId) {
      contactsArr.push({
        contactId: selectedSecondaryContact.contactId,
        role: ROLES.CONTRACTCOSIGNER,
      });
    }

    setIsSaving(true);

    confirmContacts(
      authKey || proposalToken || "",
      prospectId,
      proposalId,
      contactsArr,
      !!proposalToken
    )
      .then(() => {
        refetch();
        refreshAndNavigateNext();
      })
      .catch((err) => {
        setError({ action: "Confirm Names Task View", error: err });
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [
    isCustomer,
    prospectId,
    offerId,
    proposalId,
    selectedPrimaryContact,
    selectedUtilityContact,
    selectedSecondaryContact,
    contactsHaveInvalidContactInfo,
    contactsHaveChanged,
    currentTask?.isComplete,
    navigateToNextTask,
    authKey,
    proposalToken,
    refetch,
    refreshAndNavigateNext,
  ]);

  useCheckoutFooter(
    isSaving ? null : (
      <CheckoutFooter
        buttonText="Confirm Names"
        onClick={handleConfirmNames}
        isDisabled={!isContactInfoConfirmed}
      />
    ),
    [handleConfirmNames, isContactInfoConfirmed, isSaving]
  );

  if (isProspectLoading || isContactsLoading || isSaving) {
    return <LoadingIndicator fullScreen />;
  }

  const wizardActionsByStep = [
    {
      secondaryAction: {
        label: "Signer(s) don’t match",
        onClick: openEditSignersSlideout,
        testId: "confirm-names-signers-dont-match",
      },
      primaryAction: {
        label: "Signer(s) match",
        onClick: handleSignersMatch,
        testId: "confirm-names-signers-match",
      },
    },
    {
      secondaryAction: {
        label: "Utility Bill Contact does not match",
        onClick: openEditSignersSlideout,
        testId: "confirm-names-utility-dont-match",
      },
      primaryAction: {
        label: "Utility Bill Contact matches",
        onClick: handleUtilityMatch,
        testId: "confirm-names-utility-match",
      },
    },
    {
      secondaryAction: {
        label: "Contact info is incorrect",
        onClick: openEditSignersSlideout,
        testId: "confirm-names-contact-incorrect",
      },
      primaryAction: {
        label: "Contact info is correct",
        onClick: () => setIsContactInfoConfirmed(true),
        testId: "confirm-names-contact-correct",
      },
    },
  ];

  const wizardActions = wizardActionsByStep[wizardCurrentStep];
  const mismatchModalConfig = mismatchModalConfigByStep[wizardCurrentStep];

  const wizardSteps = steps.map((step, index) => {
    if (index === 0) return { ...step, complete: !titleMismatchBanner };
    if (index === 1)
      return {
        ...step,
        complete: !utilityMismatchBanner && !isMissingUtilityBill,
      };
    return step;
  });

  return (
    <>
      {error && (
        <ErrorModal
          context="ConfirmNamesTaskView"
          error={error.error}
          action={error.action}
          onClose={() => setError(undefined)}
        />
      )}
      {isEditSignersSlideoutOpen && (
        <CheckoutEditSignersSlideout
          onClose={() => setIsEditSignersSlideoutOpen(false)}
          onSaveSuccess={handleSlideoutSaveSuccess}
        />
      )}
      {isMismatchModalOpen && mismatchModalConfig && (
        <ResignRiskModal
          key={mismatchModalConfig.modalTestId}
          testId={mismatchModalConfig.modalTestId}
          title={mismatchModalConfig.title}
          body={mismatchModalConfig.body}
          acknowledgeTestId={mismatchModalConfig.acknowledgeTestId}
          primaryButtonText={mismatchModalConfig.updateButtonText}
          onPrimaryClick={handleUpdateFromMismatchModal}
          onRiskResigning={handleRiskResigning}
          onClose={closeMismatchModal}
        />
      )}
      {isUtilityBillSlideoutOpen && (
        <CheckoutUtilityBillSlideout
          onClose={() => setIsUtilityBillSlideoutOpen(false)}
          onGetBillOnline={onGetBillOnline}
          onUploadSuccess={() => setShowUploadSuccessToast(true)}
          intervalDataInitialLoading={intervalDataInitialLoading}
          intervalDataStatus={intervalDataStatus}
        />
      )}
      {showGetBillOnlineIframe && (
        <UtilityConnectionContainer
          src={billOnlineUrl || ""}
          isLoading={billOnlineLoading}
          onClose={() => {
            setShowGetBillOnlineIframe(false);
            setIsUtilityBillSlideoutOpen(true);
            startPolling();
          }}
          onError={() => setShowGetBillOnlineIframe(false)}
          returnButtonText="Back"
        />
      )}
      <SnackbarOverlay>
        <Snackbar
          type="info"
          colorScheme="dark"
          open={showUpdateInfoSnackbar}
          onClose={() => setShowUpdateInfoSnackbar(false)}
          autoHideDuration={10000}
          message={updateInfoSnackbarMessage}
        />
        <Snackbar
          type="success"
          colorScheme="dark"
          open={showUploadSuccessToast}
          onClose={() => setShowUploadSuccessToast(false)}
          message="Utility bill uploaded successfully"
        />
      </SnackbarOverlay>
      <WizardCard
        steps={wizardSteps}
        currentStep={wizardCurrentStep}
        onBack={() => setWizardCurrentStep((step) => Math.max(0, step - 1))}
        {...wizardActions}
      >
        <StepContent>
          {wizardCurrentStep === 0 && (
            <NameComparisonStep
              title="Agreement signer must be on title"
              description="Please confirm that the name of the signer or co-signer is on the title."
              leftLabel="Signer name"
              leftNames={signerNames}
              rightLabel="Names on title"
              rightNames={namesOnTitle}
              onUpdate={openEditSignersSlideout}
              mismatchBanner={
                hasTitleContractReviewBanners ? undefined : titleMismatchBanner
              }
              contractReviewBanners={
                <ContractReviewBanners
                  messages={contractReviewMessages}
                  category="customer_name_check"
                  filter={(msg) => !isTrustAgreementNameMatchMessage(msg)}
                  sessionDismissScopeId={proposalId}
                  bannerTypeOverride={BannerTypes.ErrorLight}
                />
              }
            />
          )}
          {wizardCurrentStep === 1 && (
            <NameComparisonStep
              title="Net metering agreement signer must be on utility bill"
              description="Confirm that the name on the utility bill matches the contact below."
              leftLabel="Net metering agreement signer"
              leftNames={nemSignerNames}
              rightLabel="Name on utility bill"
              rightNames={
                isMissingUtilityBill
                  ? [NAME_ON_BILL_UNKNOWN]
                  : namesOnUtilityBill
              }
              onUpdate={openEditSignersSlideout}
              mismatchBanner={
                isMissingUtilityBill || hasUtilityContractReviewBanners
                  ? undefined
                  : utilityMismatchBanner
              }
              missingBanner={
                isMissingUtilityBill
                  ? {
                      title: MISSING_UTILITY_BILL_BANNER_TITLE,
                      bannerTestId: "confirm-names-missing-utility-banner",
                      actionLabel: "Upload utility bill",
                      onAction: openUtilityBillSlideout,
                      actionTestId: "confirm-names-upload-utility-bill",
                    }
                  : undefined
              }
              titleEmptyContent={
                isMissingUtilityBill ? undefined : (
                  <NameOnBillNotFound billUrl={mostRecentUtilityBillUrl} />
                )
              }
              contractReviewBanners={
                <ContractReviewBanners
                  messages={contractReviewMessages}
                  category="nem_agreement_check"
                  filter={(message) => isNemAgreementCheckMessage(message)}
                  sessionDismissScopeId={proposalId}
                  bannerTypeOverride={BannerTypes.ErrorLight}
                />
              }
            />
          )}
          {wizardCurrentStep === 2 && (
            <ContactInfoStep
              name={signerName || "-"}
              phone={primaryPhone ? formatPhoneNumber(primaryPhone) : "-"}
              email={primaryEmail || "-"}
              onUpdate={openEditSignersSlideout}
              validationStatus={primaryValidationStatus}
              isDuplicatePhone={isDuplicatePhoneValidationStatus(
                primaryValidationStatus
              )}
              isDuplicateEmail={isDuplicateEmailValidationStatus(
                primaryValidationStatus
              )}
            />
          )}
        </StepContent>
      </WizardCard>
    </>
  );
};

const StepContent = styled.div`
  width: 100%;
`;

const SnackbarOverlay = styled.div`
  position: relative;
  z-index: 10000;
`;

export { ConfirmNamesV2 };
